"use client";

import * as React from "react";

/** Radix put these on the content; Base UI reports the same three events as
 *  `onOpenChange` reasons on the root. Calling `preventDefault()` on the event
 *  keeps the overlay open, exactly as it did under Radix. */
export interface DismissalProps {
  onEscapeKeyDown?: (event: CustomEvent<{ originalEvent: Event }>) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: Event }>) => void;
  onFocusOutside?: (event: CustomEvent<{ originalEvent: Event }>) => void;
  onInteractOutside?: (event: CustomEvent<{ originalEvent: Event }>) => void;
}

/** Radix put these on the content too; Base UI expresses them as `initialFocus`
 *  / `finalFocus` on the popup, where returning `false` means "don't move
 *  focus" — the same thing `preventDefault()` did on the Radix event. */
export interface AutoFocusProps {
  onOpenAutoFocus?: (event: CustomEvent) => void;
  onCloseAutoFocus?: (event: CustomEvent) => void;
}

interface ChangeEventDetails {
  reason: string;
  event: Event;
  cancel: () => void;
}

type DismissalRegister = (props: DismissalProps) => void;

const DismissalContext = React.createContext<DismissalRegister | null>(null);

const DISMISSAL_EVENT_TYPES: Record<string, string> = {
  "escape-key": "dismissableLayer.escapeKeyDown",
  "outside-press": "dismissableLayer.pointerDownOutside",
  "focus-out": "dismissableLayer.focusOutside",
};

function dismissalEvent(type: string, originalEvent: Event) {
  return new CustomEvent<{ originalEvent: Event }>(type, {
    cancelable: true,
    detail: { originalEvent },
  });
}

function notify(
  handlers: DismissalProps,
  reason: string,
  event: CustomEvent<{ originalEvent: Event }>,
) {
  if (reason === "escape-key") {
    handlers.onEscapeKeyDown?.(event);
    return;
  }
  if (reason === "outside-press") {
    handlers.onPointerDownOutside?.(event);
  } else {
    handlers.onFocusOutside?.(event);
  }
  handlers.onInteractOutside?.(event);
}

/** Wraps a root's `onOpenChange` so the content's dismissal callbacks fire
 *  before Base UI acts, and returns the registration the content calls.
 *  Render `DismissalProvider` with it around the Base UI root. */
export function useDismissalBridge<Details extends ChangeEventDetails>(
  onOpenChange?: (open: boolean, eventDetails: Details) => void,
): [DismissalRegister, (open: boolean, eventDetails: Details) => void] {
  const handlers = React.useRef<DismissalProps>({});

  const register = React.useCallback((props: DismissalProps) => {
    handlers.current = props;
  }, []);

  const handleOpenChange = React.useCallback(
    (open: boolean, eventDetails: Details) => {
      const type = DISMISSAL_EVENT_TYPES[eventDetails.reason];

      if (!open && type) {
        const event = dismissalEvent(type, eventDetails.event);
        notify(handlers.current, eventDetails.reason, event);
        if (event.defaultPrevented) {
          eventDetails.cancel();
          return;
        }
      }

      onOpenChange?.(open, eventDetails);
    },
    [onOpenChange],
  );

  return [register, handleOpenChange];
}

export function DismissalProvider({
  register,
  children,
}: {
  register: DismissalRegister;
  children: React.ReactNode;
}) {
  return (
    <DismissalContext.Provider value={register}>
      {children}
    </DismissalContext.Provider>
  );
}

/** Hands a content's dismissal callbacks to the enclosing root's bridge. */
export function useRegisterDismissal({
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
}: DismissalProps) {
  const register = React.useContext(DismissalContext);
  React.useEffect(() => {
    register?.({
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
    });
    return () => register?.({});
  }, [
    register,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
  ]);
}

type FocusHandler = (interactionType: unknown) => boolean | null | void;

/** Turns a Radix auto-focus callback into the Base UI focus prop. A cancelled
 *  event becomes `false` ("leave focus alone"); otherwise we must ask for the
 *  default explicitly, because `undefined` also means "leave focus alone". */
export function autoFocusProp(
  handler: ((event: CustomEvent) => void) | undefined,
): FocusHandler | undefined {
  if (!handler) return undefined;
  return (interactionType) => {
    const event = new CustomEvent("autoFocus", { cancelable: true });
    handler(event);
    if (event.defaultPrevented) return false;
    // `true` = Base UI's default focus. `null` also means "default" and is kept for
    // touch, where Base UI's own resolver focuses the popup; in 1.7.0 it acts as `true`.
    return interactionType === "touch" ? null : true;
  };
}
